import React, { useState, useRef, useCallback } from "react";
import { Upload, Link2, X, Image, Loader2, AlertCircle } from "lucide-react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { toast } from "sonner";
import axios from "axios";
import { API } from "../App";

const ALLOWED_TYPES = ["image/jpeg", "image/jpg", "image/png", "image/webp"];
const MAX_SIZE = 10 * 1024 * 1024; // 10MB

const ProductImageManager = ({ images, onImagesChange, token }) => {
  const [activeTab, setActiveTab] = useState("upload");
  const [urlInput, setUrlInput] = useState("");
  const [uploading, setUploading] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const fileInputRef = useRef(null);

  const handleUpload = useCallback(async (files) => {
    if (!files || files.length === 0) return;

    const validFiles = [];
    for (const file of Array.from(files)) {
      if (!ALLOWED_TYPES.includes(file.type)) {
        toast.error(`${file.name}: Unsupported format. Use JPG, PNG, or WEBP.`);
        continue;
      }
      if (file.size > MAX_SIZE) {
        toast.error(`${file.name}: File too large. Maximum 10MB.`);
        continue;
      }
      if (file.size === 0) {
        toast.error(`${file.name}: Empty file.`);
        continue;
      }
      validFiles.push(file);
    }

    if (validFiles.length === 0) return;

    setUploading(true);
    const newUrls = [];

    for (const file of validFiles) {
      try {
        const formData = new FormData();
        formData.append("file", file);
        const res = await axios.post(`${API}/admin/upload-image`, formData, {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "multipart/form-data",
          },
        });
        const imageUrl = `${API}/files/${res.data.path}`;
        newUrls.push(imageUrl);
      } catch (err) {
        const msg = err.response?.data?.detail || "Upload failed";
        toast.error(`${file.name}: ${msg}`);
      }
    }

    if (newUrls.length > 0) {
      onImagesChange([...images, ...newUrls]);
      toast.success(`${newUrls.length} image${newUrls.length > 1 ? "s" : ""} uploaded`);
    }
    setUploading(false);
  }, [images, onImagesChange, token]);

  const handleAddUrl = useCallback(() => {
    const url = urlInput.trim();
    if (!url) return;
    if (!url.startsWith("http://") && !url.startsWith("https://")) {
      toast.error("Please enter a valid URL starting with http:// or https://");
      return;
    }
    onImagesChange([...images, url]);
    setUrlInput("");
    toast.success("Image URL added");
  }, [urlInput, images, onImagesChange]);

  const handleRemove = useCallback((index) => {
    const updated = images.filter((_, i) => i !== index);
    onImagesChange(updated);
  }, [images, onImagesChange]);

  const handleDrag = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    if (e.dataTransfer.files?.length > 0) {
      handleUpload(e.dataTransfer.files);
    }
  }, [handleUpload]);

  return (
    <div className="border border-gray-200 rounded-lg p-4" data-testid="product-image-manager">
      <h3 className="font-medium text-gray-900 mb-4 flex items-center gap-2">
        <Image className="w-4 h-4" /> Product Images
      </h3>

      {/* Tab Switcher */}
      <div className="flex border-b border-gray-200 mb-4">
        <button
          type="button"
          data-testid="image-tab-upload"
          onClick={() => setActiveTab("upload")}
          className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
            activeTab === "upload"
              ? "border-primary text-primary"
              : "border-transparent text-gray-500 hover:text-gray-700"
          }`}
        >
          <Upload className="w-3.5 h-3.5 inline mr-1.5 -mt-0.5" />
          Upload Image
        </button>
        <button
          type="button"
          data-testid="image-tab-url"
          onClick={() => setActiveTab("url")}
          className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
            activeTab === "url"
              ? "border-primary text-primary"
              : "border-transparent text-gray-500 hover:text-gray-700"
          }`}
        >
          <Link2 className="w-3.5 h-3.5 inline mr-1.5 -mt-0.5" />
          Image URL
        </button>
      </div>

      {/* Upload Tab */}
      {activeTab === "upload" && (
        <div
          data-testid="image-upload-area"
          onDragEnter={handleDrag}
          onDragLeave={handleDrag}
          onDragOver={handleDrag}
          onDrop={handleDrop}
          onClick={() => !uploading && fileInputRef.current?.click()}
          className={`border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition-colors ${
            dragActive
              ? "border-primary bg-primary/5"
              : "border-gray-300 hover:border-gray-400 bg-gray-50"
          } ${uploading ? "opacity-50 pointer-events-none" : ""}`}
        >
          <input
            ref={fileInputRef}
            type="file"
            accept=".jpg,.jpeg,.png,.webp"
            multiple
            className="hidden"
            data-testid="image-file-input"
            onChange={(e) => {
              handleUpload(e.target.files);
              e.target.value = "";
            }}
          />
          {uploading ? (
            <div className="flex flex-col items-center gap-2">
              <Loader2 className="w-8 h-8 text-primary animate-spin" />
              <p className="text-sm text-gray-600">Uploading...</p>
            </div>
          ) : (
            <div className="flex flex-col items-center gap-2">
              <Upload className="w-8 h-8 text-gray-400" />
              <p className="text-sm text-gray-600">
                {dragActive ? "Drop images here" : "Click or drag images here to upload"}
              </p>
              <p className="text-xs text-gray-400">JPG, PNG, WEBP — Max 10MB each</p>
            </div>
          )}
        </div>
      )}

      {/* URL Tab */}
      {activeTab === "url" && (
        <div data-testid="image-url-area">
          <Label className="text-gray-700 text-sm">Image URL</Label>
          <div className="flex gap-2 mt-1">
            <Input
              value={urlInput}
              onChange={(e) => setUrlInput(e.target.value)}
              placeholder="https://example.com/image.jpg"
              className="bg-white border-gray-200 flex-1"
              data-testid="image-url-input"
              onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), handleAddUrl())}
            />
            <Button
              type="button"
              onClick={handleAddUrl}
              variant="outline"
              size="sm"
              data-testid="image-url-add-btn"
              className="shrink-0"
            >
              Add
            </Button>
          </div>
          <p className="text-xs text-gray-400 mt-1">Press Enter or click Add to include the URL</p>
        </div>
      )}

      {/* Image Previews */}
      {images.length > 0 && (
        <div className="mt-4" data-testid="image-preview-section">
          <Label className="text-gray-700 text-sm mb-2 block">
            {images.length} image{images.length > 1 ? "s" : ""} attached
          </Label>
          <div className="flex gap-3 flex-wrap">
            {images.map((url, idx) => (
              <div
                key={`${url}-${idx}`}
                data-testid={`image-preview-${idx}`}
                className={`relative group rounded-lg overflow-hidden border-2 ${
                  idx === 0 ? "border-primary" : "border-gray-200"
                }`}
              >
                <img
                  src={url}
                  alt={`Product ${idx + 1}`}
                  className="w-20 h-20 object-cover"
                  onError={(e) => {
                    e.target.src = "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='80' height='80'%3E%3Crect fill='%23f3f4f6' width='80' height='80'/%3E%3Ctext x='40' y='44' text-anchor='middle' fill='%239ca3af' font-size='10'%3EError%3C/text%3E%3C/svg%3E";
                  }}
                />
                {idx === 0 && (
                  <span className="absolute top-0 left-0 bg-primary text-white text-[10px] px-1.5 py-0.5 font-medium">
                    Main
                  </span>
                )}
                <button
                  type="button"
                  onClick={() => handleRemove(idx)}
                  data-testid={`image-remove-${idx}`}
                  className="absolute top-0 right-0 bg-red-500 text-white p-0.5 rounded-bl opacity-0 group-hover:opacity-100 transition-opacity"
                >
                  <X className="w-3.5 h-3.5" />
                </button>
              </div>
            ))}
          </div>
          <p className="text-xs text-gray-400 mt-2">First image is the main product image. Hover to remove.</p>
        </div>
      )}

      {images.length === 0 && (
        <div className="mt-3 flex items-center gap-1.5 text-xs text-amber-600">
          <AlertCircle className="w-3.5 h-3.5" />
          <span>No images added yet. Products display better with at least one image.</span>
        </div>
      )}
    </div>
  );
};

export default ProductImageManager;
